import { useState } from "react";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

export default function ContactUs() {
  const [lang, setLang] = useState(localStorage.getItem("language") || "en");
  const es = lang === "es";

  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [status, setStatus] = useState(null); // null | "sending" | "success" | "error"

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${API_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus("success");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 shadow-sm px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/assets/images/Payclear800.png" alt="PayClear" className="h-8" />
        </Link>
        <button
          onClick={() => { const n = lang === "en" ? "es" : "en"; setLang(n); localStorage.setItem("language", n); }}
          className="text-sm font-medium text-gray-500 hover:text-gray-800 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
        >
          {es ? "🇺🇸 English" : "🇲🇽 Español"}
        </button>
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-lg">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            {es ? "Contáctanos" : "Contact Us"}
          </h1>
          <p className="text-gray-500 text-sm mb-6">
            {es
              ? "¿Tienes preguntas o comentarios? Escríbenos y te responderemos pronto."
              : "Have a question or feedback? Send us a message and we'll get back to you."}
          </p>

          {status === "success" ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                {es ? "¡Mensaje enviado!" : "Message sent!"}
              </h2>
              <p className="text-gray-500 text-sm mb-6">
                {es ? "Nos pondremos en contacto contigo pronto." : "We'll get back to you soon."}
              </p>
              <button
                onClick={() => setStatus(null)}
                className="text-sm text-green-600 hover:underline"
              >
                {es ? "Enviar otro mensaje" : "Send another message"}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{es ? "Nombre" : "Name"}</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder={es ? "Tu nombre" : "Your name"}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{es ? "Correo electrónico" : "Email"}</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder={es ? "tu@email.com" : "you@example.com"}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{es ? "Asunto" : "Subject"}</label>
                <input
                  type="text"
                  name="subject"
                  required
                  value={form.subject}
                  onChange={handleChange}
                  placeholder={es ? "¿En qué podemos ayudarte?" : "How can we help?"}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{es ? "Mensaje" : "Message"}</label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder={es ? "Escribe tu mensaje aquí..." : "Write your message here..."}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 resize-none"
                />
              </div>
              {status === "error" && (
                <p className="text-sm text-red-500">
                  {es ? "Algo salió mal. Inténtalo de nuevo." : "Something went wrong. Please try again."}
                </p>
              )}
              <button
                type="submit"
                disabled={status === "sending"}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-xl transition text-sm disabled:opacity-50"
              >
                {status === "sending" ? (es ? "Enviando..." : "Sending...") : (es ? "Enviar mensaje" : "Send Message")}
              </button>
            </form>
          )}
        </div>
      </div>

      <footer className="bg-gray-900 text-gray-500 text-xs py-4 px-6 text-center">
        &copy; {new Date().getFullYear()} PayClear Inc.
      </footer>
    </div>
  );
}
